import {
  Activity,
  Award,
  BarChart3,
  Calendar,
  Clock,
  Eye,
  Hash,
  Heart,
  RefreshCw,
  TrendingUp,
  Users,
  Zap,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import RealTimeDashboardSkeleton from "./skeletons/RealTimeDashboardSkeleton";
import { SectionHeader, Tooltip } from "../../components";
import analyticsService from "../../services/analyticsService";
import { DashboardData } from "../../types/analytics";
import { cn } from "../../utils/cn";
import Button from "../ui/Button";
import Card from "../ui/Card";

interface RealTimeDashboardProps {
  initialData?: DashboardData | null;
  autoRefresh?: boolean;
  onAutoRefreshChange?: (value: boolean) => void;
}

const RealTimeDashboard: React.FC<RealTimeDashboardProps> = ({
  initialData,
  autoRefresh = false,
  onAutoRefreshChange,
}) => {
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(
    initialData ?? null,
  );
  const [loading, setLoading] = useState(!initialData);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdate, setLastUpdate] = useState<Date>(new Date());

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      const data = await analyticsService.getPerformanceDashboard();
      if (data && data.metrics) {
        setDashboardData(data);
      } else {
        throw new Error("Ecosistema de dados em sincronização.");
      }
      setLastUpdate(new Date());
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Falha na conexão de dados.",
      );
    } finally {
      setLoading(false);
    }
  };

  // Only fetch on mount if no initialData was provided
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!initialData) {
      fetchDashboardData();
    }
  }, []);

  // Sync initialData updates (e.g. parent refreshed)
  useEffect(() => {
    if (initialData) {
      setDashboardData(initialData);
      setLoading(false);
    }
  }, [initialData]);

  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      if (!document.hidden) {
        fetchDashboardData();
      }
    }, 30000);

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        fetchDashboardData();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [autoRefresh]);

  const formatNumber = (num: number): string => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + "M";
    if (num >= 1000) return (num / 1000).toFixed(1) + "K";
    return num.toString();
  };

  if (loading && !dashboardData) {
    return <RealTimeDashboardSkeleton />;
  }

  if (error && !dashboardData) {
    return (
      <Card className="p-3 border-rose-100 bg-rose-50/20 text-center">
        <Activity className="h-12 w-12 text-rose-500 mx-auto mb-2" />
        <h3 className="text-xl font-black text-gray-900 mb-2 uppercase tracking-tight">
          Ocorrência Detectada
        </h3>
        <p className="text-sm font-bold text-rose-600 mb-2">{error}</p>
        <Button
          onClick={fetchDashboardData}
          className="bg-rose-600 hover:bg-rose-700 h-10 px-3 rounded-xl font-black uppercase text-[10px] tracking-widest"
        >
          Reiniciar Fluxo
        </Button>
      </Card>
    );
  }

  return (
    <div className="space-y-2 animate-in fade-in slide-in-from-bottom-4 duration-700">
      {/* Control Header — mesmo tratamento visual do header de "Perfil
          Estratégico do ICP" (EmpathyMapDisplay.tsx): SectionHeader real
          (caixa de ícone, título/subtítulo, borda inferior) dentro do seu
          próprio card, independente do grid de KPIs abaixo. */}
      <div className="bg-white border border-purple-200 rounded-xl p-3 shadow-xs">
        <SectionHeader
          icon={<Activity className="h-5 w-5" />}
          title="Live Feedback"
          subtitle={`Refresco Automático: ${lastUpdate.toLocaleTimeString("pt-BR")} • ${dashboardData?.period ?? "N/D"}`}
          hideBorder
          action={
            <div className="flex items-center gap-2 self-center">
              <Tooltip
                content={
                  autoRefresh
                    ? "Desativar atualização automática"
                    : "Ativar atualização automática (30s)"
                }
                position="top"
              >
                <button
                  onClick={() => onAutoRefreshChange?.(!autoRefresh)}
                  aria-label={
                    autoRefresh
                      ? "Desativar atualização automática"
                      : "Ativar atualização automática"
                  }
                  className={`px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest border transition-all whitespace-nowrap ${
                    autoRefresh
                      ? "bg-purple-600 border-purple-600 text-white shadow-xs"
                      : "bg-white border-purple-200 text-purple-600 hover:bg-purple-50"
                  }`}
                >
                  {autoRefresh ? "Live ●" : "Auto-Sync"}
                </button>
              </Tooltip>
              <Tooltip content="Atualizar agora">
                <button
                  onClick={fetchDashboardData}
                  disabled={loading}
                  aria-label="Atualizar agora"
                  className="p-2 bg-gray-50 text-gray-400 hover:text-purple-600 hover:bg-purple-100 rounded-lg transition-all border border-purple-200"
                >
                  <RefreshCw
                    className={`h-4 w-4 ${loading ? "animate-spin" : ""}`}
                  />
                </button>
              </Tooltip>
            </div>
          }
        />
      </div>

      {/* Core Metrics Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
        {[
          {
            label: "Posts Ativos",
            val:
              dashboardData?.metrics?.total_posts !== undefined &&
              dashboardData?.metrics?.total_posts !== null
                ? String(dashboardData.metrics.total_posts)
                : "N/D",
            icon: BarChart3,
            color: "blue",
          },
          {
            label: "Engajamento Absoluto",
            val:
              dashboardData?.metrics?.total_engagement !== undefined &&
              dashboardData?.metrics?.total_engagement !== null
                ? formatNumber(dashboardData.metrics.total_engagement)
                : "N/D",
            icon: Heart,
            color: "rose",
          },
          {
            label: "Afinidade Média",
            val:
              dashboardData?.metrics?.avg_engagement_rate !== undefined &&
              dashboardData?.metrics?.avg_engagement_rate !== null
                ? `${dashboardData.metrics.avg_engagement_rate.toFixed(2)}%`
                : "N/D",
            icon: TrendingUp,
            color: "emerald",
          },
          {
            label: "Impressões Brutas",
            val:
              dashboardData?.metrics?.total_reach !== undefined &&
              dashboardData?.metrics?.total_reach !== null
                ? formatNumber(dashboardData.metrics.total_reach)
                : "N/D",
            icon: Eye,
            color: "purple",
          },
        ].map((m, i) => {
          const KPI_COLORS = {
            blue: {
              border: "border-blue-200",
              text: "text-blue-700",
              iconBg: "bg-blue-100",
              iconText: "text-blue-600",
            },
            rose: {
              border: "border-rose-200",
              text: "text-rose-700",
              iconBg: "bg-rose-100",
              iconText: "text-rose-600",
            },
            emerald: {
              border: "border-emerald-200",
              text: "text-emerald-700",
              iconBg: "bg-emerald-100",
              iconText: "text-emerald-600",
            },
            purple: {
              border: "border-purple-200",
              text: "text-purple-700",
              iconBg: "bg-purple-100",
              iconText: "text-purple-600",
            },
          };
          const s = KPI_COLORS[m.color as keyof typeof KPI_COLORS];
          return (
            <div
              key={i}
              className={cn(
                "relative bg-white rounded-xl border p-4 shadow-xs transition-all duration-200 overflow-hidden hover:-translate-y-0.5",
                s.border,
              )}
            >
              <div
                className={cn(
                  "absolute top-0 left-0 right-0 h-[3px]",
                  s.iconBg,
                )}
              />
              <div className="flex items-start justify-between pt-0.5">
                <div className="flex flex-col min-w-0">
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5 truncate">
                    {m.label}
                  </p>
                  <h3
                    className={cn(
                      "text-2xl font-black tracking-tight leading-none tabular-nums",
                      s.text,
                    )}
                  >
                    {m.val}
                  </h3>
                </div>
                <div
                  className={cn(
                    "w-9 h-9 rounded-lg flex items-center justify-center shrink-0",
                    s.iconBg,
                    s.iconText,
                  )}
                >
                  <m.icon className="h-4 w-4" />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Behavioral Insights */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
        <Card className="p-3 border-purple-200">
          <SectionHeader
            icon={<Users className="h-5 w-5" />}
            title="Padrões de Atividade"
            subtitle="Comportamento da audiência em tempo real"
          />
          <div className="grid grid-cols-2 gap-4">
            {[
              {
                label: "Pico de Tráfego",
                val:
                  dashboardData?.audience_insights?.most_active_time ?? "N/D",
                icon: Clock,
                color: "indigo",
              },
              {
                label: "Janela de Conversão",
                val: dashboardData?.audience_insights?.most_active_day ?? "N/D",
                icon: Calendar,
                color: "emerald",
              },
              {
                label: "Direcionamento",
                val:
                  dashboardData?.audience_insights?.engagement_trend ===
                  undefined
                    ? "N/D"
                    : dashboardData.audience_insights?.engagement_trend === "up"
                      ? "Ascendente"
                      : dashboardData.audience_insights?.engagement_trend ===
                          "down"
                        ? "Descendente"
                        : "Estável",
                icon: TrendingUp,
                color: "rose",
              },
              {
                label: "Crescimento Orgânico",
                val: (() => {
                  const gr = dashboardData?.audience_insights?.growth_rate;
                  if (typeof gr !== "number" || !Number.isFinite(gr))
                    return "N/D";
                  return `+${gr}%`;
                })(),
                icon: Zap,
                color: "amber",
              },
            ].map((insight, idx) => {
              const bgMapInsight = {
                indigo: "bg-indigo-50/30",
                emerald: "bg-emerald-50/30",
                rose: "bg-rose-50/30",
                amber: "bg-amber-50/30",
              };
              const textMapInsight = {
                indigo: "text-indigo-600",
                emerald: "text-emerald-600",
                rose: "text-rose-600",
                amber: "text-amber-600",
              };
              const iconColorMap = {
                indigo: "text-indigo-500",
                emerald: "text-emerald-500",
                rose: "text-rose-500",
                amber: "text-amber-500",
              };
              const hoverBorderMap = {
                indigo: "hover:border-purple-200",
                emerald: "hover:border-emerald-200",
                rose: "hover:border-rose-200",
                amber: "hover:border-amber-200",
              };
              return (
                <div
                  key={idx}
                  className={cn(
                    "p-3 border border-transparent rounded-lg transition-all",
                    bgMapInsight[insight.color as keyof typeof bgMapInsight],
                    hoverBorderMap[
                      insight.color as keyof typeof hoverBorderMap
                    ],
                  )}
                >
                  <div className="flex items-center gap-3 mb-2">
                    <insight.icon
                      size={14}
                      className={
                        iconColorMap[insight.color as keyof typeof iconColorMap]
                      }
                    />
                    <span className="text-[9px] font-black uppercase tracking-widest text-gray-400">
                      {insight.label}
                    </span>
                  </div>
                  <p
                    className={cn(
                      "text-sm font-black uppercase tracking-tight",
                      textMapInsight[
                        insight.color as keyof typeof textMapInsight
                      ],
                    )}
                  >
                    {insight.val}
                  </p>
                </div>
              );
            })}
          </div>
        </Card>

        <Card className="p-3 border-purple-200 bg-gray-50/50">
          <SectionHeader
            icon={<Activity className="h-5 w-5" />}
            title="Vetor de Performance (7 Dias)"
            subtitle="Engajamento diário na última semana"
          />
          <div className="space-y-3">
            {!dashboardData?.daily_performance ||
            dashboardData.daily_performance.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-center text-gray-400">
                <Activity
                  size={24}
                  className="mb-2 text-gray-300 animate-pulse"
                />
                <p className="text-xs font-semibold">
                  Sem dados históricos no período
                </p>
              </div>
            ) : (
              dashboardData.daily_performance.map((day, idx) => (
                <div key={idx} className="flex items-center gap-4 group">
                  <div className="w-12 text-center">
                    <p className="text-[9px] font-black uppercase text-gray-400">
                      {new Date(day.date).toLocaleDateString("pt-BR", {
                        weekday: "short",
                      })}
                    </p>
                    <p className="text-[10px] font-black text-gray-900">
                      {new Date(day.date).getDate()}/
                      {new Date(day.date).getMonth() + 1}
                    </p>
                  </div>
                  <div className="flex-1 h-2 bg-white rounded-full overflow-hidden p-0.5 border border-purple-200">
                    <div
                      className="h-full bg-purple-500 rounded-full transition-all duration-1000"
                      style={{
                        width: `${Math.min(100, ((day.engagement || 0) / (dashboardData?.metrics?.total_engagement || 500)) * 500)}%`,
                      }}
                    />
                  </div>
                  <span className="text-[10px] font-black text-purple-600 w-10 text-right">
                    {day.engagement ?? 0}
                  </span>
                </div>
              ))
            )}
          </div>
        </Card>
      </div>

      {/* Distributions and Spotlight */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-2">
        <Card className="p-3 border-purple-200 lg:col-span-2">
          <SectionHeader
            icon={<Hash className="h-5 w-5" />}
            title="Performance Colateral (Tags)"
            subtitle="Hashtags com maior tração no período"
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {!dashboardData?.hashtag_performance ||
            dashboardData.hashtag_performance.length === 0 ? (
              <div className="col-span-2 flex flex-col items-center justify-center gap-2 py-8 px-4 text-center">
                <div className="w-10 h-10 rounded-xl bg-purple-50 flex items-center justify-center">
                  <Hash size={18} className="text-purple-300" />
                </div>
                <p className="text-xs font-black text-gray-700">
                  Sem hashtags no período
                </p>
                <p className="text-[10px] font-semibold text-gray-400 max-w-[220px] leading-relaxed">
                  Publique posts com hashtags para ver o desempenho de cada uma
                  aqui.
                </p>
              </div>
            ) : (
              dashboardData.hashtag_performance.map((h, i) => (
                <div
                  key={i}
                  className="flex items-center justify-between p-2.5 bg-white border border-purple-200 rounded-lg group hover:border-purple-300 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 rounded-full bg-purple-400 group-hover:scale-150 transition-transform" />
                    <span className="text-xs font-black text-gray-900 tracking-tight">
                      #{h.hashtag || "—"}
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-[10px] font-black text-gray-400">
                      {h.usage ?? 0}x
                    </span>
                    <span className="text-xs font-black text-purple-600">
                      {typeof h.avg_engagement === "number" &&
                      !Number.isNaN(h.avg_engagement)
                        ? `${h.avg_engagement.toFixed(1)}%`
                        : "0%"}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        </Card>

        <Card className="p-3 border-purple-200 relative overflow-hidden group">
          <div className="absolute -top-12 -right-12 w-40 h-40 bg-emerald-500/5 rounded-full blur-3xl group-hover:bg-emerald-500/10 transition-all duration-700" />
          <SectionHeader
            icon={<Award className="h-5 w-5" />}
            title="Alpha Content"
            subtitle="Post com melhor conversão do período"
            iconBgClass="bg-emerald-500"
          />
          {dashboardData?.metrics?.best_performing_post ? (
            <div className="space-y-2">
              <p className="text-xs font-bold text-gray-600 leading-relaxed italic line-clamp-4">
                &ldquo;
                {dashboardData.metrics.best_performing_post.content || "—"}
                &rdquo;
              </p>
              <div className="pt-3 border-t border-purple-200 flex items-center justify-between">
                <div className="flex flex-col">
                  <span className="text-[10px] font-black uppercase tracking-widest text-gray-400">
                    Conversão Média
                  </span>
                  <span className="text-2xl font-black text-emerald-600 tracking-tighter">
                    {typeof dashboardData.metrics.best_performing_post
                      .engagement_rate === "number" &&
                    !Number.isNaN(
                      dashboardData.metrics.best_performing_post
                        .engagement_rate,
                    )
                      ? `${dashboardData.metrics.best_performing_post.engagement_rate.toFixed(1)}%`
                      : "N/D"}
                  </span>
                </div>
                <div className="p-3 bg-emerald-50 rounded-lg">
                  <TrendingUp size={20} className="text-emerald-500" />
                </div>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center gap-2 py-6 px-4 text-center">
              <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center">
                <Award size={18} className="text-emerald-300" />
              </div>
              <p className="text-xs font-black text-gray-700">
                Nenhum post em destaque ainda
              </p>
              <p className="text-[10px] font-semibold text-gray-400 max-w-[220px] leading-relaxed">
                Assim que houver posts publicados com engajamento no período, o
                de melhor conversão aparece aqui.
              </p>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default RealTimeDashboard;
